export interface PaginationMeta {
  page?: number;
  limit?: number;
  total?: number;
  total_pages?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  [key: string]: unknown;
}

export interface ApiErrorObject {
  message?: string;
  code?: string;
  details?: unknown;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: PaginationMeta | null;
  error?: ApiErrorObject | string | null;
}

export interface LegacyApiResponse<T> {
  data: T;
  total?: number;
  pagination?: {
    total?: number;
    page?: number;
    per_page?: number;
    has_more?: boolean;
  };
}

export type CombinedApiResponse<T> = ApiResponse<T> | LegacyApiResponse<T> | T;

export interface AddDeviceRequest {
  model: string;
  serial_number: string;
  hardware_version?: string;
  firmware_version?: string;
  is_active?: boolean;
}

export interface AssignSongRequest {
  song_id: string;
}

function isApiEnvelope<T>(payload: CombinedApiResponse<T>): payload is ApiResponse<T> {
  return Boolean(payload && typeof payload === "object" && "success" in (payload as object));
}

function isLegacyEnvelope<T>(payload: CombinedApiResponse<T>): payload is LegacyApiResponse<T> {
  return Boolean(payload && typeof payload === "object" && "data" in (payload as object));
}

export function normalizeApiResponse<T>(payload: CombinedApiResponse<T>): ApiResponse<T> {
  if (isApiEnvelope<T>(payload)) {
    return {
      success: payload.success,
      data: payload.data,
      meta: payload.meta ?? null,
      error: payload.error ?? null,
    };
  }

  if (isLegacyEnvelope<T>(payload)) {
    const legacyMeta: PaginationMeta | null = payload.pagination
      ? {
          page: payload.pagination.page,
          limit: payload.pagination.per_page,
          total: payload.pagination.total,
        }
      : payload.total !== undefined
        ? { total: payload.total }
        : null;

    return {
      success: true,
      data: payload.data,
      meta: legacyMeta,
      error: null,
    };
  }

  return {
    success: true,
    data: payload as T,
    meta: null,
    error: null,
  };
}
